import { ChatMessage } from "@/components/pages/ai-mentor/TextAgentPage";
import { UserData } from "@/types/user-data";
import AIleafGold from "@/components/ui/AIleafGold.svg";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import "./html-default-styles.css";
import { Loader2, Mic } from "lucide-react";
import { format } from "date-fns";

export const ChatHistory = ({
  messages,
  isLoading,
  userName,
  isInitialization = false,
  revisionKey = "",
  className,
  autoScroll = true,
  isVoiceMode = false,
}: {
  messages: ChatMessage[];
  isLoading: boolean;
  userName: UserData["user_name"];
  isInitialization?: boolean;
  revisionKey?: string;
  className?: string;
  autoScroll?: boolean;
  isVoiceMode?: boolean;
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const behavior = useRef<"smooth" | "instant">("smooth");
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    behavior.current = "instant";
  }, [revisionKey]);

  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const hasNewMessage = messages.length > prevCount;
    const shouldAutoScroll = hasNewMessage && prevCount > 0;

    if (autoScroll && shouldAutoScroll && messagesEndRef.current && messages.length > 0 && !isInitialization) {
      messagesEndRef.current.scrollIntoView({ behavior: behavior.current });
      behavior.current = "smooth";
    }

    prevMessageCountRef.current = messages.length;
  }, [messages, isInitialization, autoScroll]);

  const isEmptyVoiceChat = isVoiceMode && !messages.length && !isLoading && !isInitialization;

  return (
    <div className={cn("relative flex-1 space-y-4 overflow-y-auto px-4 pt-2 md:px-6 md:pt-3", className)}>
      {isInitialization && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {isEmptyVoiceChat && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Mic className="w-1/2 h-1/2 max-w-24 max-h-24 text-[#C58C10] opacity-10" />
        </div>
      )}

      {!isInitialization &&
        messages.map((msg, index) => {
          const isNewDay =
            index !== 0 && msg.timestamp.toLocaleDateString() !== messages[index - 1].timestamp.toLocaleDateString();

          return (
            <div key={msg.id}>
              {isNewDay && (
                <div className="flex items-center my-14 px-12">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <time className="px-4 text-xs text-gray-400 whitespace-nowrap ">
                    {format(msg.timestamp, "EEEE, d-MMM-yyyy")}
                  </time>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>
              )}

              <div
                className={cn("flex items-start space-x-3", { "flex-row-reverse space-x-reverse": msg.role === "user" })}
              >
                <Avatar role={msg.role} userName={userName} />
                <MessageBubble role={msg.role} message={msg} />
              </div>
            </div>
          );
        })}

      {isLoading && (
        <div className="flex items-start space-x-3">
          <Avatar role="assistant" />
          <MessageBubble role="assistant" placeholder="Thinking..." />
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

const MessageBubble = ({
  role,
  message,
  placeholder = "",
}: {
  role: "user" | "assistant";
  message?: ChatMessage;
  placeholder?: string;
}) => {
  const isAI = role === "assistant";

  return (
    <div className="flex-1 relative">
      <div
        className={cn(
          "text-sm md:text-base rounded-2xl p-3 py-2 md:p-4 max-w-[80%] md:max-w-[60%] w-fit min-w-[50%] lg:min-w-[400px]",
          {
            "bg-gray-100 rounded-tl-sm text-gray-700": isAI,
            "bg-bodhi-teal text-white rounded-tr-sm ml-auto": !isAI,
          }
        )}
      >
        <div className="chat-message-content" dangerouslySetInnerHTML={{ __html: message?.content || placeholder }} />
        <time className="text-xs opacity-70 mt-2 block">
          {message ? message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
        </time>
      </div>
    </div>
  );
};

const Avatar = ({ role, userName = "" }: { role: "user" | "assistant"; userName?: string }) => {
  const isAI = role === "assistant";
  return (
    <div
      className={cn("w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center flex-shrink-0", {
        "bg-bodhi-teal": !isAI,
        "bg-white": isAI,
      })}
    >
      {isAI ? (
        <img src={AIleafGold} alt="AI Leaf Gold" width={24} height={24} className="w-6 h-6 md:w-8 md:h-8" />
      ) : (
        <span className="text-white text-xs md:text-sm font-medium">{userName.trim()[0].toUpperCase()}</span>
      )}
    </div>
  );
};
